import React, { useMemo, useState } from 'react';
import { CalendarX2, Check, MapPin, SlidersHorizontal, SortAsc, X } from 'lucide-react';
import EventCard from '@/components/EventCard';
import { EmptyState, Section, Shimmer } from '@/components/ui';
import { QUICK_FILTERS, SORTS, applyFilters, collectEventTypes, isUpcoming } from '@/lib/filters';

const Dropdown = ({ label, icon: Icon, active, children, open, onToggle }) => (
  <div className="relative">
    <button
      type="button"
      onClick={onToggle}
      // The text label is hidden on phones, which left an unnamed icon button.
      aria-label={label}
      aria-expanded={open}
      className={`lk-focus flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors active:scale-95 sm:px-4 ${
        active
          ? 'border-brand-primary bg-brand-primary text-white'
          : 'border-ink-200 bg-white text-ink-700 hover:bg-surface-muted'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
    {open && (
      <>
        <div className="fixed inset-0 z-20" onClick={onToggle} />
        <div className="absolute right-0 z-30 mt-2 max-h-80 w-56 overflow-y-auto rounded-xl border border-ink-200 bg-white py-1 shadow-cardHover">
          {children}
        </div>
      </>
    )}
  </div>
);

const MenuItem = ({ selected, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-surface-muted"
  >
    <span className={selected ? 'font-semibold text-ink-900' : 'text-ink-700'}>{children}</span>
    {selected && <Check className="h-4 w-4 text-brand-secondary" />}
  </button>
);

/**
 * The catalog grid — filters, sorting and the chip row all computed from the
 * live event list, no hardcoded categories anywhere.
 *
 * Shared by the home page and each sub-category landing page, which differ only
 * in their heading and the list they are handed. Keeping one implementation is
 * the point: two copies of a filter bar drift, and the sub-category page would
 * quietly lose whatever the home page gained.
 */
const AllEvents = ({
  events,
  loading,
  query = '',
  types = [],
  onTypesChange,
  id = 'all-events',
  title = 'All Events',
  subtitle,
  emptyTitle = 'No events match those filters',
  // Shown above the grid, e.g. when a city borrows another city's events.
  notice,
  emptyMessage = 'Try clearing a filter or picking a different date — new events go live every week.',
}) => {
  const [quick, setQuick] = useState([]);
  const [sort, setSort] = useState('recommended');
  const [menu, setMenu] = useState(null);

  const upcoming = useMemo(() => events.filter(isUpcoming), [events]);
  const availableTypes = useMemo(() => collectEventTypes(upcoming), [upcoming]);

  const visible = useMemo(
    () => applyFilters(upcoming, { query, quick, types, sort }),
    [upcoming, query, quick, types, sort]
  );

  const toggleQuick = (key) =>
    setQuick((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleType = (type) =>
    onTypesChange?.(types.includes(type) ? types.filter((t) => t !== type) : [...types, type]);

  const hasFilters = quick.length > 0 || types.length > 0;
  const clearAll = () => {
    setQuick([]);
    onTypesChange?.([]);
  };

  return (
    <Section
      id={id}
      title={title}
      subtitle={
        loading
          ? 'Loading events…'
          : subtitle ??
            `${visible.length} event${visible.length === 1 ? '' : 's'}${query ? ` for “${query}”` : ''}`
      }
      className="bg-white"
      action={
        <div className="flex items-center gap-2">
          <Dropdown
            label={SORTS[sort].label}
            icon={SortAsc}
            active={sort !== 'recommended'}
            open={menu === 'sort'}
            onToggle={() => setMenu(menu === 'sort' ? null : 'sort')}
          >
            {Object.entries(SORTS).map(([key, cfg]) => (
              <MenuItem
                key={key}
                selected={sort === key}
                onClick={() => {
                  setSort(key);
                  setMenu(null);
                }}
              >
                {cfg.label}
              </MenuItem>
            ))}
          </Dropdown>
          {availableTypes.length > 0 && (
            <Dropdown
              label="Filters"
              icon={SlidersHorizontal}
              active={types.length > 0}
              open={menu === 'type'}
              onToggle={() => setMenu(menu === 'type' ? null : 'type')}
            >
              {availableTypes.map((type) => (
                <MenuItem key={type} selected={types.includes(type)} onClick={() => toggleType(type)}>
                  {type}
                </MenuItem>
              ))}
            </Dropdown>
          )}
        </div>
      }
    >
      {notice && (
        <p role="status" className="mb-5 flex items-start gap-2 rounded-2xl bg-brand-tint px-4 py-3 text-sm text-ink-700">
          <MapPin className="mt-0.5 h-4 w-4 flex-none text-brand-secondary" aria-hidden="true" />
          <span>{notice}</span>
        </p>
      )}

      {/* Quick filter chips */}
      <div className="scrollbar-hide lk-fade-r -mx-4 mb-6 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 sm:[mask-image:none] sm:[-webkit-mask-image:none]">
        {QUICK_FILTERS.map((filter) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => toggleQuick(filter.key)}
            className={`lk-chip ${quick.includes(filter.key) ? 'lk-chip-active' : 'lk-chip-idle'}`}
          >
            {filter.label}
          </button>
        ))}
        {types.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => toggleType(type)}
            className="lk-chip border-brand-secondary bg-brand-tint text-brand-secondary"
          >
            {type}
            <X className="h-3.5 w-3.5" />
          </button>
        ))}
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="lk-focus min-h-[44px] whitespace-nowrap rounded-full px-3 text-sm font-semibold text-brand-secondary hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 min-[480px]:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white">
              <Shimmer className="aspect-[3/4] rounded-none" />
              <div className="space-y-2 p-4">
                <Shimmer className="h-3 w-24" />
                <Shimmer className="h-4 w-3/4" />
                <Shimmer className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          icon={CalendarX2}
          title={emptyTitle}
          message={emptyMessage}
          action={
            hasFilters ? (
              <button type="button" onClick={clearAll} className="lk-btn-primary">
                Clear filters
              </button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 min-[480px]:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {visible.map((event, i) => (
            <EventCard key={event.id} event={event} index={i} />
          ))}
        </div>
      )}
    </Section>
  );
};

export default AllEvents;
